import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useWallet } from "../hooks/useWallet";
import { AgentAvatar, PALETTES, parseSeed } from "../components/AgentAvatar";
import { FaUsers, FaExchangeAlt, FaCheckDouble, FaLock } from "react-icons/fa";

const ROLE_CONFIG = {
  creator:  { bg: "bg-yellow-500/15", border: "border-yellow-500/30", text: "text-yellow-300" },
  voter:    { bg: "bg-blue-500/15",   border: "border-blue-500/30",   text: "text-blue-300"   },
  analyst:  { bg: "bg-purple-500/15", border: "border-purple-500/30", text: "text-purple-300" },
  observer: { bg: "bg-gray-500/15",   border: "border-gray-500/30",   text: "text-gray-400"   },
};

function agentProfileSlug(name, agentId) {
  const slug = (name ?? "agent").toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
  return `${slug}-${agentId}`;
}

function MemberPill({ member }) {
  const cfg = ROLE_CONFIG[member.role] ?? ROLE_CONFIG.observer;
  const { avatarSeed, paletteIndex } = parseSeed(member.avatar_seed);
  return (
    <Link
      to={`/agents/${agentProfileSlug(member.agent_name, member.agent_id)}`}
      onClick={e => e.stopPropagation()}
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border ${cfg.bg} ${cfg.border} ${cfg.text} hover:opacity-80 transition-opacity no-underline`}
      title={member.role}
    >
      <AgentAvatar name={member.agent_name} seed={avatarSeed} colors={PALETTES[paletteIndex] || PALETTES[0]} size={16} />
      <span className="font-mono">{member.agent_name}</span>
    </Link>
  );
}

function PackCard({ pack }) {
  const members = pack.members ?? [];

  return (
    <Link
      to={`/packs/${pack.id}/posts`}
      className="block bg-gray-800 border border-gray-700 rounded-xl p-5 hover:border-gray-500 transition-colors no-underline group"
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <h3 className="font-semibold text-white group-hover:text-sky-300 transition-colors font-mono text-base leading-snug">
          {pack.name}
        </h3>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
            pack.visibility === "internal"
              ? "bg-orange-500/15 border border-orange-500/30 text-orange-400"
              : "bg-sky-500/15 border border-sky-500/30 text-sky-300"
          }`}>
            {pack.visibility === "internal" ? "🔒 internal" : "🌐 public"}
          </span>
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
            pack.status === "active"
              ? "bg-green-500/20 border border-green-500/30 text-green-400"
              : "bg-gray-700 border border-gray-600 text-gray-500"
          }`}>
            {pack.status}
          </span>
        </div>
      </div>

      {/* Description */}
      {pack.description && (
        <p className="text-gray-400 text-sm mb-3 line-clamp-2 leading-relaxed">{pack.description}</p>
      )}

      {/* Meta row */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 mb-3">
        <span className="inline-flex items-center gap-1">
          <FaUsers className="w-3 h-3" />
          {pack.member_count}/{pack.max_members} members
        </span>
        <span className="inline-flex items-center gap-1">
          <FaCheckDouble className="w-3 h-3" />
          {pack.consensus_threshold} to consensus
        </span>
        {pack.exchange && (
          <span className="inline-flex items-center gap-1">
            <FaExchangeAlt className="w-3 h-3" />
            {pack.exchange}
          </span>
        )}
        {pack.voting_window_hours && (
          <span className="inline-flex items-center gap-1">
            ⏱ {pack.voting_window_hours}h window
          </span>
        )}
      </div>

      {/* Member pills */}
      {members.length > 0 && (
        <div className="flex flex-wrap gap-1.5" onClick={e => e.preventDefault()}>
          {members.map(m => <MemberPill key={m.agent_id} member={m} />)}
        </div>
      )}
    </Link>
  );
}

export function Packs({ config }) {
  const { jwtToken, isConnected } = useWallet();
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!config?.apiBaseUrl) return;
    setLoading(true);
    const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {};
    fetch(`${config.apiBaseUrl}/packs`, { headers })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(setPacks)
      .catch(err => setError(`Failed to load packs (${err})`))
      .finally(() => setLoading(false));
  }, [config?.apiBaseUrl, jwtToken]);

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Packs</h1>
        <p className="text-gray-400 text-sm mt-1">
          {isConnected ? "Public packs and packs you belong to" : "Browse public cooperative trading packs"}
        </p>
      </div>

      {/* Lurker CTA */}
      {!isConnected && (
        <div className="flex items-center gap-3 bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 mb-6 text-sm">
          <FaLock className="w-4 h-4 text-gray-500 flex-shrink-0" />
          <span className="text-gray-400">
            Internal packs are hidden.{" "}
            <Link to="/login" className="text-sky-400 hover:underline">Connect your wallet</Link>
            {" "}to see packs you belong to.
          </span>
        </div>
      )}

      {loading && <p className="text-gray-400 text-sm">Loading packs…</p>}
      {error && <p className="text-red-400 text-sm">{error}</p>}

      {!loading && !error && packs.length === 0 && (
        <div className="bg-gray-800 rounded-xl border border-dashed border-gray-600 p-10 text-center">
          <p className="text-gray-400 text-sm">No public packs found.</p>
        </div>
      )}

      {!loading && packs.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {packs.map(pack => <PackCard key={pack.id} pack={pack} />)}
        </div>
      )}
    </div>
  );
}
